import styled, { css, keyframes } from 'styled-components';

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

/** Small inline "updating" spinner — inherits color from its container. */
export const Spinner = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

/* Visual tokens kept in step with PandaDocAnalytics.styled.ts so the two
   PandaDoc sections look like one product. */

export const Wrapper = styled.div`
  width: 100%;
  margin: 32px 0 64px;
  color: #0f172a;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

export const SubTitle = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-bottom: 16px;
`;

export const BackLink = styled.button`
  align-self: flex-start;
  margin-bottom: 12px;
  padding: 0;
  border: none;
  background: none;
  color: #4f46e5;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const GhostButton = styled.button`
  margin-left: auto;
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  cursor: pointer;

  &:hover {
    border-color: #cbd5e1;
    color: #0f172a;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

/** «Обновить данные» — triggers the PandaDoc backend sync; shows progress. */
export const SyncButton = styled(GhostButton)`
  margin-left: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const Card = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 12px;
`;

export const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 16px;
  margin: 12px 0;
`;

export const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ControlLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
`;

export const Select = styled.select`
  height: 34px;
  min-width: 160px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #0f172a;
  background: #fff;
`;

export const NumberInput = styled.input`
  height: 34px;
  width: 140px;
  padding: 0 10px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #0f172a;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 600;
    padding: 10px 8px;
    border-bottom: 1px solid #e2e8f0;
  }

  tbody td {
    padding: 10px 8px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

/** A whole row dimmed because the document is already provisioned. */
export const TableRow = styled.tr<{ dim?: boolean }>`
  ${({ dim }) =>
    dim &&
    css`
      opacity: 0.55;
    `}
`;

export const FolderRow = styled.tr`
  cursor: pointer;
`;

export const CellInput = styled.input<{ invalid?: boolean }>`
  width: 100%;
  min-width: 110px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid ${({ invalid }) => (invalid ? '#dc2626' : '#e2e8f0')};
  border-radius: 6px;
  font-size: 13px;
  color: #0f172a;
  background: ${({ invalid }) => (invalid ? '#fef2f2' : '#fff')};

  &:disabled {
    background: #f8fafc;
    color: #94a3b8;
  }
`;

export const NameCellGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 170px;
`;

export const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

export const ContractLink = styled.a`
  color: #4f46e5;
  font-weight: 600;
  font-size: 12px;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
`;

export const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

type BadgeVariant = 'ok' | 'warn' | 'neutral' | 'error' | 'info';

const BADGE_COLORS: Record<BadgeVariant, { bg: string; fg: string }> = {
  ok: { bg: '#dcfce7', fg: '#166534' },
  warn: { bg: '#fef3c7', fg: '#92400e' },
  error: { bg: '#fee2e2', fg: '#991b1b' },
  info: { bg: '#ede9fe', fg: '#5b21b6' },
  neutral: { bg: '#f1f5f9', fg: '#475569' },
};

export const Badge = styled.span<{ variant: BadgeVariant }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
  background: ${({ variant }) => BADGE_COLORS[variant].bg};
  color: ${({ variant }) => BADGE_COLORS[variant].fg};
`;

export const FieldHint = styled.div<{ error?: boolean }>`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ error }) => (error ? '#dc2626' : '#94a3b8')};
`;

export const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
`;

export const PrimaryButton = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: 8px;
  background: #4f46e5;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #4338ca;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const SecondaryButton = styled.button`
  padding: 9px 18px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: #fff;
  color: #0f172a;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    border-color: #94a3b8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
`;

export const CenterLoader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 320px;

  & > div {
    width: 48px;
    height: 48px;
  }
`;

export const ResultPanel = styled.div`
  margin-top: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
`;

export const ResultTitle = styled.div`
  font-size: 14px;
  font-weight: 650;
  margin-bottom: 8px;
`;

export const ResultItem = styled.div<{ variant: BadgeVariant }>`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 0;
  font-size: 13px;
  border-top: 1px solid #f1f5f9;

  &:first-of-type {
    border-top: none;
  }
`;

export const ValidationBanner = styled.div`
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #991b1b;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 600;
`;
